import { Role, WoPermission } from '../graphql/generated';

import { RoleType } from './permissions.types';

import { RequiredPermissionsMatrix } from '@iss/libs/auth';

export type Permission =
  | 'can see request comment'
  | 'can cancel request'
  | 'can upload order document'
  | 'can create a new order'
  | 'can create a new order without pricing'
  | 'can create a new order with sub-services'
  | 'can create a new order without start date limit'
  | 'can cancel scheduled order without date limit'
  | 'can create a manual order'
  | 'can create a new order without confirmation'
  | 'can create schedule orders'
  | 'can manage upload weight'
  | 'can display all waste types in the selector'
  | 'can display all containers in the selector'
  | 'can manage suppliers'
  | 'can create a new supplier'
  | 'can edit a supplier'
  | 'can delete a supplier'
  | 'can manage customers'
  | 'can create a new customer'
  | 'can edit a customer'
  | 'can delete a customer'
  | 'can manage sites'
  | 'can delete equipment'
  | 'can edit equipment'
  | 'can create a new equipment'
  | 'can create invoice statement'
  | 'can manage invoicing'
  | 'can manage reports'
  | 'can manage equipment reports'
  | 'can manage equipment internal reports'
  | 'can manage MST reports'
  | 'can manage CSL reports'
  | 'can manage financial reports'
  | 'can manage customer reports'
  | 'can manage admin page'
  | 'can manage users'
  | 'can see processes'
  | 'can manage purchase price'
  | 'can manage sales price'
  | 'can delete site'
  | 'can edit site'
  | 'can manage customer documents'
  | 'can make manual order adjustments'
  | 'can download order documents'
  | 'can accept and reject order negotiations'
  | 'can accept and reject scheduled order negotiations'
  | 'can accept and reject request negotiations'
  | 'can propose another request dates'
  | 'can make credit memo'
  | 'can create a new site'
  | 'can impersonate'
  | 'can confirm execution'
  | 'can see history order details'
  | 'can see financial sales prices'
  | 'can see financial purchase prices'
  | 'can see financial gross margin'
  | 'can see group order comment'
  | 'can display all suppliers in the selector'
  | 'can download schedules template'
  ;

export const PERM: RequiredPermissionsMatrix<Permission> = {
  'can see request comment': WoPermission.RequestReadComment,
  'can cancel request': WoPermission.OrderCancel,
  'can upload order document': WoPermission.GroupOrderDocumentsUpload,
  'can create a new order': {
    anyOf: [
      RoleType.Admin,
      Role.CentralWasteTeamAdmin,
      Role.CentralWasteTeamOperations,
      Role.Customer,
      Role.CustomerApprover,
      Role.CustomerExternal,
      Role.CustomerExternalApprover,
    ],
  },
  'can create a new order without pricing': WoPermission.OrderCreateWithoutPricing,
  'can create a new order with sub-services': WoPermission.OrderCreateWithAddedServices,
  'can create a new order without start date limit': WoPermission.OrderCreateIgnoreSla,
  'can cancel scheduled order without date limit': WoPermission.OrderCancelWithoutSla,
  'can create a manual order': WoPermission.OrderCreateOutsideCatalog,
  'can create a new order without confirmation': {
    anyOf: [
      RoleType.Admin,
      Role.CentralWasteTeamAdmin,
      Role.CentralWasteTeamOperations,
    ],
  },
  'can create schedule orders': {
    anyOf: [
      RoleType.Admin,
      Role.CentralWasteTeamAdmin,
      Role.CentralWasteTeamOperations,
      Role.Customer,
      Role.CustomerApprover,
      Role.CustomerExternal,
      Role.CustomerExternalApprover,
    ],
  },
  'can manage upload weight': {
    anyOf: [
      RoleType.Admin,
      Role.Supplier,
    ],
  },
  'can display all waste types in the selector': {
    anyOf: [
      RoleType.Admin,
      Role.CentralWasteTeamAdmin,
      Role.CentralWasteTeamOperations,
      Role.Customer,
      Role.CustomerApprover,
    ],
  },
  'can display all containers in the selector': {
    anyOf: [
      RoleType.Admin,
      Role.CentralWasteTeamAdmin,
      Role.CentralWasteTeamOperations,
      Role.Customer,
      Role.CustomerApprover,
    ],
  },
  'can manage suppliers': {
    anyOf: [
      RoleType.Admin,
      RoleType.CentralTeam,
      RoleType.Supplier,
    ],
  },
  'can create a new supplier': WoPermission.SupplierCreate,
  'can edit a supplier': WoPermission.SupplierEdit,
  'can delete a supplier': WoPermission.SupplierDelete,
  'can manage customers': {
    anyOf: [
      RoleType.Admin,
      RoleType.CentralTeam,
      RoleType.Customer,
    ],
  },
  'can create a new customer': WoPermission.CustomerCreate,
  'can edit a customer': WoPermission.CustomerEdit,
  'can delete a customer': WoPermission.CustomerDelete,
  'can manage sites': {
    anyOf: [
      RoleType.Admin,
      Role.CentralWasteTeamAdmin,
      Role.CentralWasteTeamOperations,
      RoleType.Customer,
    ],
  },
  'can delete equipment': WoPermission.CustomerSiteEquipmentCrud,
  'can edit equipment': WoPermission.CustomerSiteEquipmentCrud,
  'can create a new equipment': WoPermission.CustomerSiteEquipmentCrud,
  'can create invoice statement': {
    anyOf: [
      WoPermission.InvoicingCustomerCreate,
      WoPermission.InvoicingSupplierCreate,
    ],
  },
  'can manage invoicing': {
    anyOf: [
      WoPermission.InvoicingCustomerCreate,
      WoPermission.InvoicingCustomerPreview,
    ],
  },
  'can manage reports': {
    anyOf: [
      WoPermission.ReportEquipment,
      WoPermission.ReportEquipmentInternal,
      WoPermission.ReportFinancial,
      WoPermission.ReportCustomer,
    ],
  },
  'can manage equipment reports': WoPermission.ReportEquipment,
  'can manage equipment internal reports': WoPermission.ReportEquipmentInternal,
  'can manage MST reports': WoPermission.ReportMst,
  'can manage CSL reports': WoPermission.ReportCustomersSitesLocations,
  'can manage financial reports': WoPermission.ReportFinancial,
  'can manage customer reports': WoPermission.ReportCustomer,
  'can manage admin page': {
    anyOf: [
      WoPermission.AdminPurchasePricingUpload,
      WoPermission.AdminPurchasePricingDownload,
      WoPermission.AdminSalesPricingUpload,
      WoPermission.AdminSalesPricingDownload,
      WoPermission.AdminUserRead,
      WoPermission.AdminUserUpdateEmployee,
      WoPermission.AdminSuperAdmin,
    ],
  },
  'can manage users': {
    anyOf: [
      WoPermission.AdminUserRead,
      WoPermission.AdminUserUpdateEmployee,
      WoPermission.AdminSuperAdmin,
    ],
  },
  'can see processes': WoPermission.AdminProcessRead,
  'can manage purchase price': {
    anyOf: [
      WoPermission.AdminPurchasePricingUpload,
      WoPermission.AdminPurchasePricingDownload,
    ],
  },
  'can manage sales price': {
    anyOf: [
      WoPermission.AdminSalesPricingUpload,
      WoPermission.AdminSalesPricingDownload,
    ],
  },
  'can delete site': WoPermission.CustomerSiteDelete,
  'can edit site': WoPermission.CustomerSiteEdit,
  'can make manual order adjustments': WoPermission.OrderEditAfterDelivery,
  'can manage customer documents': WoPermission.DocumentList,
  'can download order documents': WoPermission.DocumentDownload,
  'can accept and reject order negotiations': {
    anyOf: [
      WoPermission.OrderEditRespondToProposal,
      RoleType.Supplier,
    ],
  },
  'can accept and reject scheduled order negotiations': {
    anyOf: [
      WoPermission.ScheduleEditRespondToProposal,
      RoleType.Supplier,
    ],
  },
  'can accept and reject request negotiations': {
    anyOf: [
      RoleType.Admin,
      Role.CentralWasteTeamAdmin,
      Role.CentralWasteTeamOperations,
      Role.Customer,
      Role.CustomerApprover,
      Role.CustomerExternal,
      Role.CustomerExternalApprover,
      RoleType.Supplier,
    ],
  },
  'can propose another request dates': RoleType.Supplier,
  'can make credit memo': WoPermission.CreditMemoCreate,
  'can create a new site': WoPermission.CustomerSiteCreate,
  'can impersonate': WoPermission.AdminImpersonate,
  'can confirm execution': WoPermission.OrderConfirmDelivery,
  'can see history order details': WoPermission.GroupOrderReadHistory,
  'can see financial sales prices': WoPermission.SalesOrderReadFinancial,
  'can see financial purchase prices': WoPermission.PurchaseOrderReadFinancial,
  'can see financial gross margin': {
    allOf: [
      WoPermission.SalesOrderReadFinancial,
      WoPermission.PurchaseOrderReadFinancial,
    ],
  },
  'can see group order comment': WoPermission.GroupOrderReadComment,
  'can display all suppliers in the selector': {
    anyOf: [
      RoleType.Admin,
      RoleType.CentralTeam,
    ],
  },
  'can download schedules template': WoPermission.DocumentTemplateDownload,
};
